// SidebarLayout.jsx
import { CloseIcon, HamburgerIcon, SearchIcon } from "@chakra-ui/icons";
import {
    Box,
    Button,
    Center,
    Collapse,
    HStack,
    IconButton,
    Input,
    InputGroup,
    InputLeftElement,
    Text,
    useColorModeValue,
    VStack,
} from "@chakra-ui/react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
    FaBookmark,
    FaCalendarCheck,
    FaCalendarPlus,
    FaCarAlt,
    FaCheck,
    FaClinicMedical,
    FaCreditCard,
    FaDochub,
    FaFileExcel,
    FaHospital,
    FaIntercom,
    FaLocationArrow,
    FaMap,
    FaMoneyBill,
    FaMoneyCheck,
    FaPrint,
    FaQuestion,
    FaRoute,
    FaShippingFast,
    FaTrash,
    FaTruck,
    FaUmbrella,
    FaUsers,
    FaVoicemail,
    FaWarehouse
} from "react-icons/fa";
import {
    FaBoxArchive,
    FaBoxesPacking,
    FaBuilding,
    FaBuildingFlag,
    FaCaretDown,
    FaCloudArrowDown,
    FaCodePullRequest,
    FaComputer,
    FaFileSignature,
    FaFlask,
    FaMessage,
    FaRulerCombined,
    FaShield,
    FaShieldHalved,
    FaShoePrints,
    FaUserAstronaut,
    FaVial
} from "react-icons/fa6";
import { GiMedicalDrip } from "react-icons/gi";
import { GoLog, GoSignOut } from "react-icons/go";
import { MdChargingStation, MdDataObject } from "react-icons/md";
import { PiUserFocus } from "react-icons/pi";
import { RiDashboardLine } from "react-icons/ri";
import { useDispatch } from "react-redux";
import { Outlet, useLocation } from "react-router-dom";
import { getAllPickups, logoutUser } from "../api";
import Loading from "../components/Loading/Loading";
import { rLogoutUser } from "../redux/actions/userActions";
import { persistor } from "../redux/store";
import { hasPermission, Logo } from "../Utils";
import { ColorModeSwitcher } from "./HomePage/ColorModeSwitcher";
import SidebarLink from "./SidebarLink";

const STORAGE_KEY = "sidebar_sections_v1";

function readSectionState() {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) return {};
        const parsed = JSON.parse(raw);
        return parsed && typeof parsed === "object" ? parsed : {};
    } catch {
        return {};
    }
}

function writeSectionState(state) {
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(state || {}));
    } catch {
        // ignore
    }
}

const SidebarLayout = () => {
    const [isSidebarVisible, setIsSidebarVisible] = useState(true);
    const [loading, setLoading] = useState(true);
    const [isHiding, setIsHiding] = useState(false);
    const [allTempPickups, setAllTempPickups] = useState([]);
    const [menuQuery, setMenuQuery] = useState("");
    const [openSections, setOpenSections] = useState(() => readSectionState());

    const location = useLocation();
    const dispatch = useDispatch();

    const headerBg = useColorModeValue("gray.800", "gray.800");
    const sidebarBg = useColorModeValue("gray.900", "gray.900");
    const hoverBg = useColorModeValue("gray.700", "gray.700");
    const textMuted = useColorModeValue("gray.400", "gray.400");

    const isMobile = useMemo(() => (typeof window !== "undefined" ? window.innerWidth < 768 : false), []);
    const tempRefreshTimerRef = useRef(null);

    const toggleSidebar = () => setIsSidebarVisible((v) => !v);

    const handleLinkClick = () => {
        if (typeof window !== "undefined" && window.innerWidth < 768) {
            setIsSidebarVisible(false);
        }
    };

    const handleLogout = async () => {
        const result = await logoutUser();
        if (result.ok) {
            dispatch(rLogoutUser());
            persistor.purge();
            window.location.href = "/login";
        } else {
            alert("Something went wrong, Try again later");
        }
    };

    const getTempPickups = useCallback(async () => {
        try {
            const response = await getAllPickups(1, 9999, "TEMP");
            if (response.success) {
                setAllTempPickups(response.data.pickups || []);
            }
        } catch {
            // ignore
        }
    }, []);

    // loading gate for route transitions (keeps your existing behavior)
    useEffect(() => {
        setLoading(true);
        const timer = setTimeout(() => setLoading(false), 300);
        return () => clearTimeout(timer);
    }, [location.pathname]);

    // initial responsive sidebar
    useEffect(() => {
        setIsSidebarVisible(typeof window !== "undefined" ? window.innerWidth >= 768 : true);

        const onResize = () => {
            const wide = window.innerWidth >= 768;
            setIsSidebarVisible(wide);
        };
        window.addEventListener("resize", onResize);
        return () => window.removeEventListener("resize", onResize);
    }, []);

    // URL param hide_side=true
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const hideSideParam = params.get("hide_side") === "true";
        setIsHiding(hideSideParam);
    }, [location.search]);

    // fetch TEMP badge: on mount + periodic + when navigating into pickups areas
    useEffect(() => {
        getTempPickups();

        if (tempRefreshTimerRef.current) clearInterval(tempRefreshTimerRef.current);
        tempRefreshTimerRef.current = setInterval(() => {
            getTempPickups();
        }, 60_000);

        return () => {
            if (tempRefreshTimerRef.current) clearInterval(tempRefreshTimerRef.current);
        };
    }, [getTempPickups]);

    useEffect(() => {
        if (location.pathname.startsWith("/pickups")) {
            getTempPickups();
        }
    }, [location.pathname, getTempPickups]);

    const toggleSection = (key) => {
        setOpenSections((prev) => {
            const next = { ...(prev || {}), [key]: !(prev && prev[key] !== undefined ? prev[key] : true) };
            writeSectionState(next);
            return next;
        });
    };

    const hasAnyPerm = (perms = []) => perms.some((p) => hasPermission(p));

    const menuConfig = useMemo(() => {
        const sections = [
            {
                key: "dashboard",
                title: null,
                alwaysShowTitle: false,
                items: [
                    {
                        type: "link",
                        label: "Dashboard",
                        to: "/dashboard",
                        icon: RiDashboardLine,
                        permAny: [], // always visible
                    },
                ],
            },

            {
                key: "bss",
                title: "BSS",
                permAny: [
                    "viewBSSClients",
                    "viewBSSSampleTypes",
                    "viewBSSSampleSizeCharts",
                    "viewBSSTestingTypes",
                    "viewBSSCOCs"
                ],
                items: [
                    { type: "link", label: "Clients", to: "/bss/clients", icon: FaBuilding, permAny: ["viewBSSClients"] },
                    { type: "link", label: "Sample Types", to: "/bss/sample-types", icon: FaVial, permAny: ["viewBSSSampleTypes"] },
                    { type: "link", label: "Sample Size Charts", to: "/bss/sample-size-charts", icon: FaRulerCombined, permAny: ["viewBSSSampleSizeCharts"] },
                    { type: "link", label: "Testing Types", to: "/bss/testing-types", icon: FaFlask, permAny: ["viewBSSTestingTypes"] },
                    { type: "link", label: "COCs", to: "/bss/cocs", icon: FaFileSignature, permAny: ["viewBSSCOCs"] },
                ],
            },


            {
                key: "printing",
                title: "Printing",
                permAny: ["labelsPrint", "dlsPrint"],
                items: [
                    { type: "link", label: "Names & DOB", to: "/labels-names", icon: FaPrint, permAny: ["labelsPrint"] },
                    { type: "link", label: "DLS Label", to: "/dls-label", icon: FaShoePrints, isImage: true, img: Logo, permAny: ["dlsPrint"] },
                ],
            },

            {
                key: "physicians_patients",
                title: "Physicians & Patients",
                permAny: ["viewPhysicians", "viewPatients"],
                items: [
                    { type: "link", label: "Physicians", to: "/reqs/physicians", icon: FaBuildingFlag, permAny: ["viewPhysicians"] },
                    { type: "link", label: "Patients", to: "/reqs/patients", icon: FaUserAstronaut, permAny: ["viewPatients"] },
                ],
            },

            {
                key: "tests_panels",
                title: "Tests & Panels",
                permAny: ["viewTestCategory", "viewPanels", "viewCptIcdCoverage", "viewTestCatalog", "viewTestOrders", "createTestOrders"],
                items: [
                    { type: "link", label: "Categories", to: "/reqs/test/categories", icon: FaBookmark, permAny: ["viewTestCategory"] },
                    { type: "link", label: "Panels", to: "/reqs/panels", icon: FaBookmark, permAny: ["viewPanels"] },
                    { type: "link", label: "ICD-10", to: "/reqs/test/coverage", icon: FaUmbrella, permAny: ["viewCptIcdCoverage"] },
                    { type: "link", label: "Tests Catalog", to: "/reqs/test/catalog", icon: MdDataObject, permAny: ["viewTestCatalog"] },
                    { type: "link", label: "Test Orders", to: "/reqs/orders/list", icon: MdChargingStation, permAny: ["viewTestOrders"] },
                    { type: "link", label: "New Test Orders", to: "/reqs/orders/create", icon: MdChargingStation, permAny: ["createTestOrders"] },
                ],
            },
            {
                key: "couriers_mgmt",
                title: "Couriers Management",
                permAny: ["viewCouriers", "viewMessages", "viewLeaveRequests", "viewCars", "createCars", "updateCars", "deleteCars", "restoreCars", "assignCars"],
                items: [
                    { type: "link", label: "Couriers", to: "/couriers/couriers", icon: FaBoxesPacking, permAny: ["viewCouriers"] },
                    { type: "link", label: "Cars", to: "/cars", icon: FaCarAlt, permAny: ["viewCars"] },
                    { type: "link", label: "Leave Requests", to: "/couriers/leave-requests", icon: FaDochub, permAny: ["viewLeaveRequests"] },
                    { type: "link", label: "Couriers Messaging", to: "/messages", icon: FaMessage, permAny: ["viewMessages"] },
                    { type: "link", label: "Couriers Schedules", to: "/schedules", icon: FaCalendarPlus, permAny: ["createSchedules"] },
                    { type: "link", label: "View Schedules", to: "/view-schedules", icon: FaCalendarCheck, permAny: ["viewSchedules"] },
                ],
            },

            {
                key: "pickups_mgmt",
                title: "Pickups Management",
                permAny: ["viewRoutes", "viewPickups", "receivePickups"],
                items: [
                    { type: "link", label: "Routes", to: "/routes/routes", icon: FaRoute, permAny: ["viewRoutes"] },
                    {
                        type: "group",
                        permAny: ["viewPickups"],
                        items: [
                            { type: "link", label: "Today Pickup", to: "/pickups", icon: FaTruck, permAny: ["viewPickups"] },
                            { type: "link", label: "Finished Pickup", to: "/pickups/finished", icon: FaCheck, permAny: ["viewPickups"] },
                            {
                                type: "link",
                                label: "Request Pickup",
                                to: "/pickups/request",
                                icon: FaQuestion,
                                permAny: ["viewPickups"],
                                badge: allTempPickups.length,
                            },
                            { type: "link", label: "Map View", to: "/map", icon: FaMap, permAny: ["viewPickups"] },
                        ],
                    },
                    { type: "link", label: "Receive Pickups", to: "/pickups/receive", icon: FaIntercom, permAny: ["receivePickups"] },
                ],
            },

            {
                key: "supplies_orders",
                title: "Supplies Orders",
                permAny: ["viewWarehouses", "viewInventory", "getAllCategories", "getAllSupplies", "viewOrders"],
                items: [
                    { type: "link", label: "Warehouses", to: "/warehouses", icon: FaWarehouse, permAny: ["viewWarehouses"] },
                    { type: "link", label: "Inventory", to: "/inventory", icon: FaBoxArchive, permAny: ["viewInventory"] },
                    { type: "link", label: "Supplies Categories", to: "/supplies-categories", icon: FaBookmark, permAny: ["getAllCategories"] },
                    { type: "link", label: "Supplies Items", to: "/supplies-items", icon: GiMedicalDrip, permAny: ["getAllSupplies"] },
                    { type: "link", label: "Clients Orders", to: "/clients-orders", icon: FaCodePullRequest, permAny: ["viewOrders"] },
                    { type: "link", label: "Create Clients Orders", to: "/create-order-internal", icon: FaShippingFast, permAny: ["viewOrders"] },
                ],
            },

            {
                key: "clients_mgmt",
                title: "Clients Management",
                permAny: ["viewFacilities", "viewLabLocation", "getAllBusiness", "viewSalesRep"],
                items: [
                    { type: "link", label: "Facilities", to: "/facilities", icon: FaBuildingFlag, permAny: ["viewFacilities"] },
                    { type: "link", label: "Labs", to: "/labs", icon: FaHospital, permAny: ["viewLabLocation"] },
                    { type: "link", label: "Clients", to: "/all-businesses", icon: FaClinicMedical, permAny: ["getAllBusiness"] },
                    { type: "link", label: "Sales Reps", to: "/sales-reps-managements", icon: FaUserAstronaut, permAny: ["viewSalesRep"] },
                ],
            },

            {
                key: "users_mgmt",
                title: "Users Management",
                permAny: ["listUsers", "viewRoles"],
                items: [
                    { type: "link", label: "Users", to: "/users", icon: FaUsers, permAny: ["listUsers"] },
                    { type: "link", label: "Roles", to: "/roles", icon: FaShield, permAny: ["viewRoles"] },
                ],
            },

            {
                key: "locations_pscs",
                title: "Locations and PSC's",
                permAny: ["viewPSCLocations", "viewInsurance", "viewPrice"],
                items: [
                    { type: "link", label: "PSC's", to: "/pscs", icon: FaLocationArrow, permAny: ["viewPSCLocations"] },
                    { type: "link", label: "Insurances", to: "/insurance", icon: FaShieldHalved, permAny: ["viewInsurance"] },
                    { type: "link", label: "Tests Prices", to: "/test-prices", icon: FaMoneyBill, permAny: ["viewPrice"] },
                ],
            },

            {
                key: "transactions",
                title: "Transactions",
                permAny: ["viewTransactions", "makeTransaction", "viewSubscriptions"],
                items: [
                    { type: "link", label: "Transactions", to: "/transactions", icon: FaMoneyCheck, permAny: ["viewTransactions"] },
                    { type: "link", label: "New Transaction", to: "/new-transactions", icon: FaCreditCard, permAny: ["makeTransaction"] },
                    { type: "link", label: "Subscriptions", to: "/transaction/subscriptions", icon: FaCaretDown, permAny: ["viewSubscriptions"] },
                ],
            },

            {
                key: "equipment_sendout",
                title: "Equipment Sendout",
                permAny: ["viewShipments"],
                items: [{ type: "link", label: "Equipments", to: "/shipments", icon: FaComputer, permAny: ["viewShipments"] }],
            },

            {
                key: "system",
                title: "System",
                permAny: ["getAllFiles", "viewReports", "viewLogs", "viewTrash", "viewTemplates"],
                items: [
                    { type: "link", label: "Files and Docs", to: "/documents", icon: FaCloudArrowDown, permAny: ["getAllFiles"] },
                    { type: "link", label: "Reports", to: "/reports", icon: FaFileExcel, permAny: ["viewReports"] },
                    { type: "link", label: "Pickup Reports", to: "/pickup-reports", icon: FaFileExcel, permAny: ["viewReports"] },
                    { type: "link", label: "Log", to: "/logs", icon: GoLog, permAny: ["viewLogs"] },
                    { type: "link", label: "Trash", to: "/trash", icon: FaTrash, permAny: ["viewTrash"] },
                    { type: "link", label: "Emails Configuration", to: "/email", icon: FaVoicemail, permAny: ["viewTemplates"] },
                ],
            },
        ];

        return sections;
    }, [allTempPickups.length]);

    const normalizedQuery = menuQuery.trim().toLowerCase();

    const filteredSections = useMemo(() => {
        const matches = (label) => {
            if (!normalizedQuery) return true;
            return String(label || "").toLowerCase().includes(normalizedQuery);
        };

        const itemVisible = (item) => {
            const perms = item.permAny || [];
            if (perms.length && !hasAnyPerm(perms)) return false;
            if (item.type === "link") return matches(item.label);
            if (item.type === "group") return (item.items || []).some(itemVisible);
            return false;
        };

        return (menuConfig || [])
            .map((sec) => {
                const secPerms = sec.permAny || [];
                const secAllowed = !secPerms.length || hasAnyPerm(secPerms);

                if (!secAllowed && sec.title) return null;

                const items = (sec.items || []).filter(itemVisible).map((it) => {
                    if (it.type !== "group") return it;
                    return { ...it, items: (it.items || []).filter(itemVisible) };
                });

                if (sec.title) {
                    if (!items.length) return null;
                    return { ...sec, items };
                }

                // title-less sections (like dashboard): keep if any item remains
                return items.length ? { ...sec, items } : null;
            })
            .filter(Boolean);
    }, [menuConfig, normalizedQuery]);

    const SectionHeader = ({ title, sectionKey }) => {
        const isOpen = openSections[sectionKey] !== false;
        return (
            <Button
                onClick={() => toggleSection(sectionKey)}
                variant="ghost"
                justifyContent="space-between"
                w="full"
                px="2"
                py="2"
                fontSize="xs"
                color={textMuted}
                fontWeight="bold"
                textTransform="uppercase"
                _hover={{ bg: hoverBg }}
                rightIcon={<FaCaretDown style={{ transform: isOpen ? "rotate(0deg)" : "rotate(-90deg)", transition: "0.15s" }} />}
            >
                <Text w="full" textAlign="left">
                    {title}
                </Text>
            </Button>
        );
    };

    return (
        <Box className="sidebar">
            {/* Header */}
            <HStack
                as="header"
                justify="space-between"
                p="4"
                bg={headerBg}
                color="white"
                position="fixed"
                width="full"
                zIndex="1000"
            >
                <HStack>
                    <IconButton
                        size="sm"
                        icon={isSidebarVisible ? <CloseIcon /> : <HamburgerIcon />}
                        bg="transparent"
                        color="white"
                        _hover={{ bg: "rgba(255,255,255,0.08)" }}
                        onClick={toggleSidebar}
                        aria-label="Toggle Sidebar"
                    />
                    <ColorModeSwitcher />
                    <Text fontSize="lg" fontWeight="bold">
                        LMS
                    </Text>
                </HStack>
            </HStack>

            <HStack spacing={0} align="stretch" height="100vh" pt="60px">
                {/* Mobile overlay */}
                {!isHiding && isSidebarVisible && (
                    <Box
                        display={{ base: "block", md: "none" }}
                        position="fixed"
                        top="60px"
                        left="0"
                        right="0"
                        bottom="0"
                        bg="blackAlpha.600"
                        zIndex="998"
                        onClick={() => setIsSidebarVisible(false)}
                    />
                )}

                {/* Sidebar */}
                {!isHiding && isSidebarVisible && (
                    <Box
                        width={{ base: "full", md: "260px" }}
                        bg={sidebarBg}
                        color="white"
                        p="4"
                        position="fixed"
                        height="calc(100vh - 60px)"
                        zIndex="999"
                        overflowY="auto"
                    >
                        <VStack align="stretch" spacing="2">
                            {/* Search */}
                            <InputGroup size="sm">
                                <InputLeftElement pointerEvents="none">
                                    <SearchIcon color="gray.400" />
                                </InputLeftElement>
                                <Input
                                    value={menuQuery}
                                    onChange={(e) => setMenuQuery(e.target.value)}
                                    placeholder="Search menu..."
                                    bg="whiteAlpha.100"
                                    borderColor="whiteAlpha.200"
                                    _hover={{ borderColor: "whiteAlpha.300" }}
                                    _focus={{ borderColor: "whiteAlpha.400" }}
                                />
                            </InputGroup>

                            {/* Menu */}
                            {filteredSections.map((sec) => {
                                const isOpen = openSections[sec.key] !== false;

                                // title-less sections render directly (Dashboard block)
                                if (!sec.title) {
                                    return (
                                        <Box key={sec.key} mt="1">
                                            {(sec.items || []).map((item, idx) => {
                                                if (item.type !== "link") return null;
                                                const perms = item.permAny || [];
                                                if (perms.length && !hasAnyPerm(perms)) return null;

                                                return (
                                                    <SidebarLink
                                                        key={`${item.to}_${idx}`}
                                                        onClick={handleLinkClick}
                                                        icon={item.icon}
                                                        isImage={item.isImage}
                                                        img={item.img}
                                                        label={item.label}
                                                        to={item.to}
                                                        badge={item.badge}
                                                    />
                                                );
                                            })}
                                        </Box>
                                    );
                                }

                                return (
                                    <Box key={sec.key} mt="2">
                                        <SectionHeader title={sec.title} sectionKey={sec.key} />
                                        <Collapse in={isOpen} animateOpacity>
                                            <VStack align="stretch" spacing="1" mt="1">
                                                {(sec.items || []).map((item, idx) => {
                                                    if (item.type === "group") {
                                                        return (item.items || []).map((sub, sIdx) => (
                                                            <SidebarLink
                                                                key={`${sub.to}_${idx}_${sIdx}`}
                                                                onClick={handleLinkClick}
                                                                icon={sub.icon}
                                                                isImage={sub.isImage}
                                                                img={sub.img}
                                                                label={sub.label}
                                                                to={sub.to}
                                                                badge={sub.badge}
                                                            />
                                                        ));
                                                    }

                                                    if (item.type !== "link") return null;

                                                    return (
                                                        <SidebarLink
                                                            key={`${item.to}_${idx}`}
                                                            onClick={handleLinkClick}
                                                            icon={item.icon}
                                                            isImage={item.isImage}
                                                            img={item.img}
                                                            label={item.label}
                                                            to={item.to}
                                                            badge={item.badge}
                                                        />
                                                    );
                                                })}
                                            </VStack>
                                        </Collapse>
                                    </Box>
                                );
                            })}

                            {/* Account */}
                            <Box mt="3">
                                <Text mb={2} fontSize="xs" color={textMuted} fontWeight="bold" textTransform="uppercase" pl="2">
                                    Account
                                </Text>

                                <SidebarLink onClick={handleLinkClick} icon={PiUserFocus} label="Your Account" to="/account" />

                                <Button
                                    onClick={handleLogout}
                                    leftIcon={<GoSignOut />}
                                    justifyContent="start"
                                    colorScheme="white"
                                    fontSize={13}
                                    variant="ghost"
                                    w="full"
                                    _hover={{ bg: hoverBg }}
                                >
                                    Signout
                                </Button>
                            </Box>
                        </VStack>
                    </Box>
                )}

                {/* Main Content */}
                <Box
                    flex="1"
                    p="6"
                    ml={{ base: "0", md: isSidebarVisible && !isHiding ? "260px" : "0" }}
                >
                    {loading ? (
                        <Center mt="20%">
                            <Loading />
                        </Center>
                    ) : (
                        <Outlet />
                    )}
                </Box>
            </HStack>
        </Box>
    );
};

export default SidebarLayout;


// import { CloseIcon, HamburgerIcon } from '@chakra-ui/icons';
// import { Box, Button, Center, HStack, IconButton, Text, VStack } from '@chakra-ui/react';
// import { useEffect, useState } from 'react';
// import { FaBookmark, FaCarAlt, FaCheck, FaClinicMedical, FaCreditCard, FaDochub, FaFileExcel, FaHospital, FaIntercom, FaLocationArrow, FaMap, FaMoneyBill, FaMoneyCheck, FaPrint, FaQuestion, FaRoute, FaShippingFast, FaTrash, FaTruck, FaUmbrella, FaUsers, FaVoicemail, FaWarehouse } from "react-icons/fa";
// import { FaBoxArchive, FaBuildingFlag, FaCaretDown, FaCloudArrowDown, FaCodePullRequest, FaComputer, FaMessage, FaShield, FaShieldHalved, FaShoePrints, FaUserAstronaut } from "react-icons/fa6";
// import { GiMedicalDrip } from "react-icons/gi";
// import { GoLog, GoSignOut } from "react-icons/go";
// import { MdChargingStation, MdDataObject } from 'react-icons/md';
// import { PiUserFocus } from "react-icons/pi";
// import { RiDashboardLine } from "react-icons/ri";
// import { useDispatch } from 'react-redux';
// import { Outlet, useLocation } from 'react-router-dom';
// import { getAllPickups, logoutUser } from '../api';
// import Loading from '../components/Loading/Loading';
// import { rLogoutUser } from '../redux/actions/userActions';
// import { persistor } from '../redux/store'; // Import persistor
// import { hasPermission, Logo } from '../Utils';
// import { ColorModeSwitcher } from './HomePage/ColorModeSwitcher';
// import SidebarLink from './SidebarLink';

// const SidebarLayout = () => {
//     const [isSidebarVisible, setIsSidebarVisible] = useState(true); // Sidebar visible by default
//     const [loading, setLoading] = useState(true); // Add loading state
//     const [isHiding, setIsHiding] = useState(false);
//     const [allTempPickups, setAllTempPickups] = useState([]);

//     const location = useLocation();
//     const dispatch = useDispatch()
//     // Function to toggle sidebar visibility
//     const toggleSidebar = () => {
//         setIsSidebarVisible(!isSidebarVisible);
//     };

//     // Set loading to false once isActive is determined on each location change
//     useEffect(() => {
//         setLoading(true);
//         getTempPickups()
//         const timer = setTimeout(() => setLoading(false), 300); // Adjust delay as needed
//         return () => clearTimeout(timer);
//     }, [location.pathname]); // Run on route change


//     useEffect(() => {
//         setIsSidebarVisible(window.innerWidth >= 768);
//     }, []);
//     // Effect to add window resize listener

//     const handleLogout = async () => {
//         const result = await logoutUser();
//         if (result.ok) {
//             // Dispatch logout action to clear Redux state
//             dispatch(rLogoutUser());

//             // Purge persisted state
//             persistor.purge();

//             // Redirect to login or home page after logout
//             window.location.href = '/login';
//         } else {
//             alert('Something went wrong, Try again later')
//         }
//     };


//     useEffect(() => {
//         const params = new URLSearchParams(location.search);
//         const hideSideParam = params.get('hide_side') === 'true';

//         // If `hide_side=true` in URL, hide the sidebar regardless of screen size
//         if (hideSideParam) {
//             setIsHiding(true);
//         } else {
//             setIsHiding(false);
//         }
//     }, [location.search]);


//     const getTempPickups = async (page = 1) => {
//         try {
//             const response = await getAllPickups(1, 9999, "TEMP");
//             if (response.success) {
//                 const pickups = response.data.pickups;
//                 setAllTempPickups(pickups);
//             }
//         } catch (error) {
//             // setError('Failed to load routes');
//         }
//         // setLoading(false);
//     };


//     const handleLinkClick = () => {
//         if (window.innerWidth < 768) {
//             setIsSidebarVisible(false);
//         }
//     };


//     // Replace your current return block with this:
//     return (
//         <Box className="sidebar">
//             {/* Header */}
//             <HStack
//                 as="header"
//                 justify="space-between"
//                 p="4"
//                 bg="gray.800"
//                 color="white"
//                 position="fixed"
//                 width="full"
//                 zIndex="1000"
//             >
//                 <HStack>
//                     <IconButton
//                         size="sm"
//                         icon={isSidebarVisible ? <CloseIcon /> : <HamburgerIcon />}
//                         style={{ background: 'none', color: "#FFF" }}
//                         onClick={toggleSidebar}
//                         aria-label="Toggle Sidebar"
//                     />
//                     <ColorModeSwitcher />
//                     <Text fontSize="lg" fontWeight="bold">LMS</Text>
//                 </HStack>
//             </HStack>

//             <HStack spacing={0} align="stretch" height="100vh" pt="60px">
//                 {/* Sidebar */}
//                 {!isHiding && isSidebarVisible && (
//                     <Box
//                         width={{ base: 'full', md: '250px' }}
//                         bg="gray.900"
//                         color="white"
//                         p="4"
//                         position="fixed"
//                         height="94vh"
//                         zIndex="999"
//                         overflowY="auto"
//                     >
//                         <VStack align="stretch" spacing="1">
//                             {/* Section: Pickups */}
//                             <Box mb={5}>
//                                 <SidebarLink onClick={handleLinkClick} icon={RiDashboardLine} label="Dashboard" to="/dashboard" />
//                             </Box>


//                             <Text mb={3} mt={5} fontSize="xs" color="gray.400" fontWeight={'bold'} textTransform="uppercase" pl="2">
//                                 Printing
//                             </Text>
//                             {hasPermission('viewShipments') && <SidebarLink onClick={handleLinkClick} icon={FaPrint} label="Names & DOB" to="/labels-names" />}
//                             {hasPermission('viewShipments') && <SidebarLink onClick={handleLinkClick} isImage={true} img={Logo} icon={FaShoePrints} label="DLS Label" to="/dls-label" />}



//                             {/* Section: LABORATORY */}
//                             {(
//                                 hasPermission('viewPhysicians') ||
//                                 hasPermission('viewPatients')
//                             ) && (
//                                     <Text mb={3} fontSize="xs" color="gray.400" fontWeight={'bold'} textTransform="uppercase" mt="4" pl="2">
//                                         Physicians & Patients
//                                     </Text>
//                                 )}
//                             {hasPermission('viewPhysicians') && <SidebarLink onClick={handleLinkClick} icon={FaBuildingFlag} label="Physicians" to="/reqs/physicians" />}
//                             {hasPermission('viewPatients') && <SidebarLink onClick={handleLinkClick} icon={FaUserAstronaut} label="Patients" to="/reqs/patients" />}


//                             {(
//                                 hasPermission('viewTestCategory') ||
//                                 hasPermission('viewCptIcdCoverage') ||
//                                 hasPermission('viewTestCatalog')
//                             ) && (
//                                     <Text mb={3} mt={5} fontSize="xs" color="gray.400" fontWeight={'bold'} textTransform="uppercase" pl="2">
//                                         Tests & Panels
//                                     </Text>
//                                 )}

//                             {hasPermission('viewTestCategory') && <SidebarLink onClick={handleLinkClick} icon={FaBookmark} label="Categories" to="/reqs/test/categories" />}
//                             {hasPermission('viewPanels') && <SidebarLink onClick={handleLinkClick} icon={FaBookmark} label="Panels" to="/reqs/panels" />}
//                             {hasPermission('viewCptIcdCoverage') && <SidebarLink onClick={handleLinkClick} icon={FaUmbrella} label="ICD-10" to="/reqs/test/coverage" />}
//                             {hasPermission('viewTestCatalog') && <SidebarLink onClick={handleLinkClick} icon={MdDataObject} label="Tests Catalog" to="/reqs/test/catalog" />}
//                             {hasPermission('viewTestOrders') && <SidebarLink onClick={handleLinkClick} icon={MdChargingStation} label="Test Orders" to="/reqs/orders/list" />}
//                             {hasPermission('createTestOrders') && <SidebarLink onClick={handleLinkClick} icon={MdChargingStation} label="New Test Orders" to="/reqs/orders/create" />}





//                             {(
//                                 hasPermission('viewCouriers') ||
//                                 hasPermission('viewMessages') ||
//                                 hasPermission('viewRoutes') ||
//                                 hasPermission('viewPickups') ||
//                                 hasPermission('receivePickups') ||
//                                 hasPermission('viewLeaveRequests')
//                             ) && (
//                                     <Text mb={3} mt={5} fontSize="xs" color="gray.400" fontWeight={'bold'} textTransform="uppercase" pl="2">
//                                         Couriers Management
//                                     </Text>
//                                 )}

//                             {hasPermission('viewCouriers') && <SidebarLink onClick={handleLinkClick} icon={FaCarAlt} label="Couriers" to="/couriers/couriers" />}
//                             {hasPermission('viewLeaveRequests') && <SidebarLink onClick={handleLinkClick} icon={FaDochub} label="Leave Requests" to="/couriers/leave-requests" />}
//                             {hasPermission('viewMessages') && <SidebarLink onClick={handleLinkClick} icon={FaMessage} label="Couriers Messaging" to="/messages" />}


//                             {/* PICKUPs */}
//                             {(
//                                 hasPermission('viewRoutes') ||
//                                 hasPermission('viewPickups') ||
//                                 hasPermission('receivePickups')
//                             ) && (
//                                     <Text mb={3} mt={5} fontSize="xs" color="gray.400" fontWeight={'bold'} textTransform="uppercase" pl="2">
//                                         Pickups Management
//                                     </Text>
//                                 )}
//                             {hasPermission('viewRoutes') && <SidebarLink onClick={handleLinkClick} icon={FaRoute} label="Routes" to="/routes/routes" />}
//                             {hasPermission('viewPickups') && (
//                                 <>
//                                     <SidebarLink onClick={handleLinkClick} icon={FaTruck} label="Today Pickup" to="/pickups" />
//                                     <SidebarLink onClick={handleLinkClick} icon={FaCheck} label="Finished Pickup" to="/pickups/finished" />
//                                     <SidebarLink onClick={handleLinkClick} icon={FaQuestion} label="Request Pickup" to="/pickups/request" badge={allTempPickups.length} />
//                                     <SidebarLink onClick={handleLinkClick} icon={FaMap} label="Map View" to="/map" />
//                                 </>
//                             )}




//                             {(
//                                 hasPermission('viewCars') ||
//                                 hasPermission('createCars') ||
//                                 hasPermission('updateCars') ||
//                                 hasPermission('deleteCars') ||
//                                 hasPermission('restoreCars') ||
//                                 hasPermission('assignCars')
//                             ) && (
//                                     <Text
//                                         mb={3}
//                                         mt={5}
//                                         fontSize="xs"
//                                         color="gray.400"
//                                         fontWeight="bold"
//                                         textTransform="uppercase"
//                                         pl="2"
//                                     >
//                                         Cars Management
//                                     </Text>
//                                 )}

//                             {hasPermission('viewCars') && (
//                                 <SidebarLink onClick={handleLinkClick} icon={FaCarAlt} label="Cars" to="/cars" />
//                             )}







//                             {hasPermission('receivePickups') && <SidebarLink onClick={handleLinkClick} icon={FaIntercom} label="Receive Pickups" to="/pickups/receive" />}





//                             {(
//                                 hasPermission('viewWarehouses') ||
//                                 hasPermission('viewInventory') ||
//                                 hasPermission('getAllCategories') ||
//                                 hasPermission('getAllSupplies') ||
//                                 hasPermission('viewOrders')
//                             ) && (
//                                     <Text mb={3} fontSize="xs" color="gray.400" fontWeight={'bold'} textTransform="uppercase" mt="4" pl="2">
//                                         Supplies Orders
//                                     </Text>
//                                 )}
//                             {hasPermission('viewWarehouses') && <SidebarLink onClick={handleLinkClick} icon={FaWarehouse} label="Warehouses" to="/warehouses" />}
//                             {hasPermission('viewInventory') && <SidebarLink onClick={handleLinkClick} icon={FaBoxArchive} label="Inventory" to="/inventory" />}
//                             {hasPermission('getAllCategories') && <SidebarLink onClick={handleLinkClick} icon={FaBookmark} label="Supplies Categories" to="/supplies-categories" />}
//                             {hasPermission('getAllSupplies') && <SidebarLink onClick={handleLinkClick} icon={GiMedicalDrip} label="Supplies Items" to="/supplies-items" />}
//                             {hasPermission('viewOrders') && <SidebarLink onClick={handleLinkClick} icon={FaCodePullRequest} label="Clients Orders" to="/clients-orders" />}
//                             {hasPermission('viewOrders') && <SidebarLink onClick={handleLinkClick} icon={FaShippingFast} label="Create Clients Orders" to="/create-order-internal" />}


//                             {/* Section: Management */}
//                             {(
//                                 hasPermission('viewFacilities') ||
//                                 hasPermission('viewLabLocation') ||
//                                 hasPermission('getAllBusiness') ||
//                                 hasPermission('viewSalesRep')
//                             ) && (
//                                     <Text mb={3} fontSize="xs" color="gray.400" fontWeight={'bold'} textTransform="uppercase" mt="4" pl="2">
//                                         Clients Management
//                                     </Text>
//                                 )}
//                             {hasPermission('viewFacilities') && <SidebarLink onClick={handleLinkClick} icon={FaBuildingFlag} label="Facilities" to="/facilities" />}
//                             {hasPermission('viewLabLocation') && <SidebarLink onClick={handleLinkClick} icon={FaHospital} label="Labs" to="/labs" />}
//                             {hasPermission('getAllBusiness') && <SidebarLink onClick={handleLinkClick} icon={FaClinicMedical} label="Clients" to="/all-businesses" />}
//                             {hasPermission('viewSalesRep') && <SidebarLink onClick={handleLinkClick} icon={FaUserAstronaut} label="Sales Reps" to="/sales-reps-managements" />}







//                             {/* Section: System */}
//                             {(
//                                 hasPermission('listUsers') ||
//                                 hasPermission('viewRoles')
//                             ) && (
//                                     <Text mb={3} fontSize="xs" color="gray.400" fontWeight={'bold'} textTransform="uppercase" mt="4" pl="2">
//                                         Users Management
//                                     </Text>
//                                 )}
//                             {hasPermission('listUsers') && <SidebarLink onClick={handleLinkClick} icon={FaUsers} label="Users" to="/users" />}
//                             {hasPermission('viewRoles') && <SidebarLink onClick={handleLinkClick} icon={FaShield} label="Roles" to="/roles" />}


//                             {(
//                                 hasPermission('viewPSCLocations') ||
//                                 hasPermission('viewInsurance') ||
//                                 hasPermission('viewPrice') ||
//                                 hasPermission('viewTransactions')
//                             ) && (
//                                     <Text mb={3} fontSize="xs" color="gray.400" fontWeight={'bold'} textTransform="uppercase" mt="4" pl="2">
//                                         Locations and PSC's
//                                     </Text>
//                                 )}
//                             {hasPermission('viewPSCLocations') && <SidebarLink onClick={handleLinkClick} icon={FaLocationArrow} label="PSC's" to="/pscs" />}
//                             {hasPermission('viewInsurance') && <SidebarLink onClick={handleLinkClick} icon={FaShieldHalved} label="Insurances" to="/insurance" />}
//                             {hasPermission('viewPrice') && <SidebarLink onClick={handleLinkClick} icon={FaMoneyBill} label="Tests Prices" to="/test-prices" />}


//                             {(
//                                 hasPermission('viewTransactions') ||
//                                 hasPermission('makeTransaction')
//                             ) && (
//                                     <Text mb={3} fontSize="xs" color="gray.400" fontWeight={'bold'} textTransform="uppercase" mt="4" pl="2">
//                                         Transactions
//                                     </Text>
//                                 )}

//                             {hasPermission('viewTransactions') &&
//                                 <>
//                                     <SidebarLink onClick={handleLinkClick} icon={FaMoneyCheck} label="Transactions" to="/transactions" />
//                                 </>
//                             }

//                             {hasPermission('makeTransaction') &&
//                                 <SidebarLink onClick={handleLinkClick} icon={FaCreditCard} label="New Transaction" to="/new-transactions" />
//                             }

//                             {hasPermission('viewSubscriptions') &&
//                                 <SidebarLink onClick={handleLinkClick} icon={FaCaretDown} label="Subscriptions" to="/transaction/subscriptions" />
//                             }




//                             {hasPermission('viewShipments') && (
//                                 <Text mb={3} fontSize="xs" color="gray.400" fontWeight={'bold'} textTransform="uppercase" mt="4" pl="2">
//                                     Equipment Sendout
//                                 </Text>
//                             )}
//                             {hasPermission('viewShipments') && <SidebarLink onClick={handleLinkClick} icon={FaComputer} label="Equipments" to="/shipments" />}

//                             {(
//                                 hasPermission('getAllFiles') ||
//                                 hasPermission('viewReports') ||
//                                 hasPermission('viewLogs') ||
//                                 hasPermission('viewTrash') ||
//                                 hasPermission('viewTemplates')
//                             ) && (
//                                     <Text mb={3} fontSize="xs" color="gray.400" fontWeight={'bold'} textTransform="uppercase" mt="4" pl="2">
//                                         System
//                                     </Text>
//                                 )}
//                             {hasPermission('getAllFiles') && <SidebarLink onClick={handleLinkClick} icon={FaCloudArrowDown} label="Files and Docs" to="/documents" />}
//                             {hasPermission('viewReports') && <SidebarLink onClick={handleLinkClick} icon={FaFileExcel} label="Reports" to="/reports" />}
//                             {hasPermission('viewReports') && <SidebarLink onClick={handleLinkClick} icon={FaFileExcel} label="Pickup Reports" to="/pickup-reports" />}
//                             {hasPermission('viewLogs') && <SidebarLink onClick={handleLinkClick} icon={GoLog} label="Log" to="/logs" />}
//                             {hasPermission('viewTrash') && <SidebarLink onClick={handleLinkClick} icon={FaTrash} label="Trash" to="/trash" />}
//                             {hasPermission('viewTemplates') && <SidebarLink onClick={handleLinkClick} icon={FaVoicemail} label="Emails Configuration" to="/email" />}

//                             {/* Section: Account */}
//                             <Text mb={3} fontSize="xs" color="gray.400" fontWeight={'bold'} textTransform="uppercase" mt="4" pl="2">Account</Text>
//                             <SidebarLink onClick={handleLinkClick} icon={PiUserFocus} label="Your Account" to="/account" />
//                             <Button
//                                 onClick={handleLogout}
//                                 leftIcon={<GoSignOut />}
//                                 justifyContent="start"
//                                 colorScheme="white"
//                                 fontSize={13}
//                                 variant="ghost"
//                                 w="full"
//                                 _hover={{ bg: 'gray.600' }}
//                             >
//                                 Signout
//                             </Button>
//                         </VStack>
//                     </Box>
//                 )}

//                 {/* Main Content */}
//                 <Box
//                     flex="1"
//                     p="6"
//                     ml={{ base: '0', md: (isSidebarVisible && !isHiding) ? '250px' : '0' }}
//                 >
//                     {loading ? (
//                         <Center mt="20%">
//                             <Loading />
//                         </Center>
//                     ) : (
//                         <Outlet />
//                     )}
//                 </Box>
//             </HStack>
//         </Box>
//     );

// };

// export default SidebarLayout;
